export function gcdiv(a:number,b:number){
  while(b != 0){
    const x = b;
    b = a % b;
    a = x;
  }
  return(a);
}

class FactorC{
  p: number
  e: number
  constructor(p:number){
    this.p = p;
    this.e = 1;
  }
}
export function factor(n: number){
  const ar:FactorC[] =  [];
  for(let p=2; p*p<=n;p+=1){
    if(n % p == 0){
      const obj = new FactorC(p);
      n /= p;
      while(n % p == 0){
	obj.e += 1;
	n /= p;
      }
      ar.push(obj);
    }
  }
  if(n != 1){
    ar.push({p:n, e:1});
  }
  return(ar);
}

export function euler(n: number){
  const ar = factor(n);
  let phi = 1;
  for(const obj of ar){
    phi *= Math.pow(obj.p,obj.e) - Math.pow(obj.p,obj.e-1);
  }
  return(phi);
}

// gausian integers
export class Gauss{
  constructor(
    public r:number,
    public i:number
  ){}

  get Rerror(){
    return(! Number.isInteger(this.r) );
  }
  get Ierror(){
    return(! Number.isInteger(this.i) );
  }
  get error(){
    return(this.Rerror || this.Ierror);
  }
  
  add(z: Gauss){
    return new Gauss(this.r + z.r, this.i + z.i);
  }
  sub(z: Gauss){
    return new Gauss(this.r - z.r, this.i - z.i);
  }
  mul(z: Gauss){
    return new Gauss(this.r*z.r - this.i*z.i, this.r*z.i + this.i*z.r);    
  }
  rmul(x:number){
    return new Gauss(this.r*x, this.i*x);    
  }
  div(z: Gauss){
    return new QGauss( this.mul(z.conj), z.norm);
  }
  copy(){
    return new Gauss(this.r,this.i);
  }

  static get zero(){
    return new Gauss(0,0);
  }
  static get one(){
    return new Gauss(1,0);
  }
  static get i(){
    return new Gauss(0,1);
  }
  static get minusOne(){
    return new Gauss(-1,0);
  }
  static get minusI(){
    return new Gauss(0,-1);
  }
  static get NaN(){
    return new Gauss(NaN,NaN);
  }
  get conj(){
    return new Gauss(this.r,-this.i);
  }
  get neg(){
    return new Gauss(-this.r,-this.i);
  }
  get norm(){
    return this.r*this.r + this.i*this.i;    
  }
  get isZero(){
    return (this.r == 0 && this.i == 0);
  }
  get txt(): string{
    if(this.r == 0){
      if(this.i >= 2){
	return this.i + 'i';
      }else if(this.i == 1){
	return 'i';
      }else if(this.i == 0){
	return '0';
      }else if(this.i == -1){
	return '- i';
      }else{
	return '- ' + (-this.i) + 'i';
      }
    }else{
      let str = '' + this.r;
      if(this.i >= 2){
	str += ' + ' + this.i + 'i';
      }else if(this.i == 1){
	str += ' + i';
      }else if(this.i == -1){
	str += ' - i';
      }else if(this.i <= -2){
	str += ' - ' + (-this.i) + 'i';
      }
      return str;
    }
  }
  get fencedTxt(): string{
    if( (this.r == 0 && this.i >= 0) || (this.r >= 0 && this.i == 0) ){
      return this.txt;
    }else{
      return '( ' + this.txt + ' )';
    }
  }
}

function intFrac(n : number, d:number): string{ // n/d
  const a = Math.floor(n/d);
  const b = n - a*d;
  if(b == 0){
    return a < 0 ? '( ' + a + ' )' : '' + a;
  }else{
    return '( ' + a + ' + ' + b + '/' + d + ' )';
  }
}

export class QGauss{
  constructor(
    public n: Gauss,
    public d: number
  ){
    if(this.d != 0){
      let g = gcdiv(this.n.r, gcdiv(this.n.i,this.d));
      if( this.d*g < 0 ){
	g *= -1;
      }
      this.n.r /= g;
      this.n.i /= g;
      this.d /= g;
    }
  }

  get error(){
    return ( this.n.error || (this.d == 0) );
  }
  get r(): number{
    return this.n.r / this.d;
  }
  get i(): number{
    return this.n.i / this.d;
  }

  static get NaN(){
    return new QGauss(Gauss.NaN,0);
  }
  add(z: QGauss){
    return new QGauss(this.n.rmul(z.d).add(z.n.rmul(this.d)),this.d*z.d);
  }
  sub(z: QGauss){
    return new QGauss(this.n.rmul(z.d).sub(z.n.rmul(this.d)),this.d*z.d);
  }
  mul(z: QGauss){
    return new QGauss(this.n.mul(z.n),this.d*z.d);
  }
  div(z: QGauss){
    return new QGauss( this.n.rmul(z.d).mul(z.n.conj), this.d*z.n.norm);
  }
  pow(n: number){
    let z = new QGauss(Gauss.one,1);
    for(let i=0; i<n; i++){
      z = z.mul(this);
    }
    return z;
  }

  get txt(): string{
    if(this.d == 0 || this.n.error){
      return 'NaN';
    }else if(this.d == 1){
      return this.n.txt;
    }else{
      return this.n.fencedTxt + ' / ' + this.d;
    }
  }
  get intTxt(): string{
    if(this.d == 0){
      return 'NaN';
    }else{
      return intFrac(this.n.r,this.d) + ' + ' + intFrac(this.n.i, this.d) + 'i';
    }    
  }
}

interface qlistI {
  q: Gauss;
  r: Gauss;
  n: number;
  valid: boolean;
  show: boolean;
}

export function mkQlist(z: Gauss, w: Gauss, flag = true): qlistI[]{
  if(w.isZero){
    return [];
  }
  const ar = new Array<qlistI>();
  const c = z.div(w);
  const a = Math.floor(c.n.r/c.d);
  const b = Math.floor(c.n.i/c.d);
  for(let x=0; x<2; x++){
    if(x == 1 && a*c.d == c.n.r){
      continue;
    }
    for(let y=0; y<2; y++){
      if(y == 1 && b*c.d == c.n.i){
	continue;
      }
      const q = new Gauss(a+x,b+y);
      const r = z.sub(w.mul(q));
      const n = r.norm;
      const valid = r.norm < w.norm;
      if(flag || valid){
	ar.push({q,r,n,valid, show: false});
      }
    }
  }
  ar.sort( (e,f) => e.n - f.n);
  if(ar.length >= 1){
    ar[0].show = true;
  }
  return ar;
}
